'use client';

import React, { type CSSProperties } from 'react';
import { useAnimation } from '@/contexts/AnimationContext';
import { cn } from '@/lib/utils';

type AnimatedContainerProps = {
  children: React.ReactNode;
  className?: string;
  animation?: 'fadeIn' | 'slideUp' | 'slideDown' | 'slideLeft' | 'slideRight' | 'scale' | 'none';
  delay?: number;
  duration?: number;
  once?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

export function AnimatedContainer({
  children,
  className,
  animation = 'fadeIn',
  delay = 0,
  duration = 0.5,
  once: _once = true,
  style,
  ...props
}: AnimatedContainerProps) {
  const { enableAnimations, prefersReducedMotion } = useAnimation();

  const shouldAnimate = enableAnimations && !prefersReducedMotion;
  const animationClass =
    shouldAnimate && animation !== 'none'
      ? {
          fadeIn: 'site-animated site-animated-fade-in',
          slideUp: 'site-animated site-animated-slide-up',
          slideDown: 'site-animated site-animated-slide-down',
          slideLeft: 'site-animated site-animated-slide-left',
          slideRight: 'site-animated site-animated-slide-right',
          scale: 'site-animated site-animated-scale',
          none: '',
        }[animation]
      : '';
  const mergedStyle = {
    ...(style ?? {}),
    '--site-anim-delay': `${delay}s`,
    '--site-anim-duration': `${duration}s`,
  } as CSSProperties;

  return (
    <div className={cn(className, animationClass)} style={mergedStyle} {...props}>
      {children}
    </div>
  );
}
